/**
 * views/states.tsx —— 交互态呈现（U-930 §4 六态；壳规范 §1ter·五；缺一即漂移）。
 *
 * 要求逐字对齐：
 *  · **空**：插画/文案 ＋ **一句下一步**；
 *  · **载入**：**骨架屏**（**禁假数据**；≤300ms 出现）；
 *  · **出错**：**错误码 ＋ 可操作按钮**（不吞错）；
 *  · **无权限**：**默认拒绝**（**不泄露存在性**）；
 *  · **超限**：与容量闸一致（满额拒 ＋ hint）；
 *  · **降级/缺席**：**记残显示**（不假装可用）。
 *
 * 纪律：本件**零取数、零业务词、零 antd 依赖**（壳首屏也用它 ⇒ 保持轻）；
 *      一切色/间距/字号经 `styles/tokens`（禁硬编码色值）。
 */
import React from "react";
import { tokenVar } from "../theme/tokens";
import type { ViewState } from "./schema";

export interface StateViewProps {
  state: ViewState;
  /** 出错/超限/降级时的结构化说明（错误码＋提示；缺省给固定文案） */
  notice?: string;
  /** 出错码（出错态必显示；缺省由宿主给，本件不吞） */
  code?: string;
  /** 可操作按钮（出错/超限/无权限：重试／查看容量／申请权限） */
  onRetry?: () => void;
  actionLabel?: string;
  /** 超限时的容量 hint（与容量闸口径一致） */
  hint?: string;
}

const BOX: React.CSSProperties = {
  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
  gap: tokenVar("space-sm"), padding: tokenVar("space-xl"), textAlign: "center",
  color: tokenVar("text-secondary"), minHeight: "120px",
};

/** 六态视觉件名（判据 `ui_six_states_visual_present` 逐件对齐） */
export const SIX_STATE_VISUALS: Record<ViewState, string> = {
  empty: "xg-state-empty",
  loading: "xg-state-skeleton",
  error: "xg-state-error",
  forbidden: "xg-state-forbidden",
  overLimit: "xg-state-over-limit",
  degraded: "xg-state-degraded",
};

/** 载入：骨架屏（形状＝呈现面，**不含任何投影值**） */
function Skeleton(): React.ReactElement {
  return (
    <div
      data-testid="state-loading"
      data-visual={SIX_STATE_VISUALS.loading}
      className="xg-skeleton"
      role="status"
      aria-busy={true}
      aria-live="off"
      aria-label="载入中"
    >
      <span className="xg-skeleton-bar" style={{ width: "60%" }} />
      <span className="xg-skeleton-bar" style={{ width: "88%" }} />
      <span className="xg-skeleton-bar" style={{ width: "72%" }} />
    </div>
  );
}

function Action(props: { label: string; onRetry?: () => void }): React.ReactElement | null {
  if (!props.onRetry) return null;
  return (
    <button type="button" className="xg-btn xg-btn-primary" data-testid="state-action" onClick={props.onRetry}>
      {props.label}
    </button>
  );
}

/** 六态统一呈现（各视图共用；版式常量不进业务样式） */
export function StateView(props: StateViewProps): React.ReactElement {
  const { state, notice } = props;
  if (state === "loading") return Skeleton();

  if (state === "empty") {
    return (
      <div data-testid="state-empty" data-visual={SIX_STATE_VISUALS.empty} className="xg-state" style={BOX}>
        <span className="xg-state-art" aria-hidden="true" />
        <strong style={{ color: tokenVar("text-primary") }}>无数据</strong>
        <span style={{ fontSize: tokenVar("font-size-secondary") }}>{notice ?? "下一步：发起新会话或选择左侧会话"}</span>
      </div>
    );
  }

  if (state === "error") {
    const code = props.code ?? "E_UNKNOWN";
    return (
      <div data-testid="state-error" data-visual={SIX_STATE_VISUALS.error} className="xg-state" style={BOX} role="alert">
        <strong style={{ color: tokenVar("state-danger") }}>出错</strong>
        <code data-testid="state-error-code" className="xg-code">{code}</code>
        <span style={{ fontSize: tokenVar("font-size-secondary") }}>{notice ?? "见错误码与提示"}</span>
        <Action label={props.actionLabel ?? "重试"} onRetry={props.onRetry} />
      </div>
    );
  }

  if (state === "forbidden") {
    // 默认拒绝：**不泄露存在性**（不说“该对象存在但无权”）
    return (
      <div data-testid="state-forbidden" data-visual={SIX_STATE_VISUALS.forbidden} className="xg-state" style={BOX}>
        <strong style={{ color: tokenVar("text-primary") }}>无权限</strong>
        <span style={{ fontSize: tokenVar("font-size-secondary") }}>默认拒绝：可申请权限后重试</span>
        <Action label={props.actionLabel ?? "申请权限"} onRetry={props.onRetry} />
      </div>
    );
  }

  if (state === "overLimit") {
    return (
      <div data-testid="state-over-limit" data-visual={SIX_STATE_VISUALS["overLimit"]} className="xg-state" style={BOX}>
        <strong style={{ color: tokenVar("state-warning") }}>超限</strong>
        <span style={{ fontSize: tokenVar("font-size-secondary") }}>{notice ?? "已达使用上限，请稍后重试"}</span>
        {props.hint ? <span className="xg-hint" data-testid="state-hint">{props.hint}</span> : null}
        <Action label={props.actionLabel ?? "查看容量"} onRetry={props.onRetry} />
      </div>
    );
  }

  return (
    <div data-testid="state-degraded" data-visual={SIX_STATE_VISUALS.degraded} className="xg-state" style={BOX}>
      <strong style={{ color: tokenVar("text-primary") }}>降级/缺席</strong>
      <span style={{ fontSize: tokenVar("font-size-secondary") }}>{notice ?? "该视图暂无数据"}</span>
    </div>
  );
}
